import React from 'react';

const RATIO_MAP: Record<string, { w: number; h: number }> = {
  '1:1': { w: 1, h: 1 },
  '3:4': { w: 3, h: 4 },
  '4:3': { w: 4, h: 3 },
  '9:16': { w: 9, h: 16 },
  '16:9': { w: 16, h: 9 },
  '3:2': { w: 3, h: 2 },
  '2:3': { w: 2, h: 3 },
  '5:4': { w: 5, h: 4 },
  '4:5': { w: 4, h: 5 },
  '21:9': { w: 21, h: 9 },
  '2:1': { w: 2, h: 1 },
};

interface AspectRatioGridProps {
  ratios: string[];
  activeRatio?: string;
  onSelect: (ratio: string) => void;
  columns?: number;
}

export const AspectRatioGrid: React.FC<AspectRatioGridProps> = ({
  ratios, activeRatio, onSelect, columns = 5,
}) => (
  <div className={`grid grid-cols-${columns} gap-1.5`}>
    {ratios.map(r => {
      const ratio = RATIO_MAP[r] || { w: 1, h: 1 };
      const isActive = activeRatio === r;
      const isLandscape = ratio.w > ratio.h;
      const isPortrait = ratio.w < ratio.h;
      const boxW = isLandscape ? 20 : isPortrait ? 12 : 16;
      const boxH = isLandscape ? 12 : isPortrait ? 20 : 16;
      return (
        <button
          key={r}
          onClick={() => onSelect(r)}
          className={`flex flex-col items-center justify-center gap-1 py-2 rounded-lg transition-all border ${isActive ? 'bg-white/10 border-white/30' : 'border-white/10 hover:border-white/20 hover:bg-white/5'}`}
        >
          <div
            className={`border rounded-sm ${isActive ? 'border-white/60' : 'border-white/30'}`}
            style={{ width: boxW, height: boxH }}
          />
          <span className={`text-[9px] ${isActive ? 'text-white/80' : 'text-white/40'}`}>{r}</span>
        </button>
      );
    })}
  </div>
);

interface ResolutionSelectorProps {
  resolutions: string[];
  allResolutions?: string[];
  activeResolution?: string;
  onSelect: (resolution: string) => void;
}

export const ResolutionSelector: React.FC<ResolutionSelectorProps> = ({
  resolutions, allResolutions, activeResolution, onSelect,
}) => {
  const displayResolutions = allResolutions && allResolutions.length > 0 ? allResolutions : resolutions;
  const availableSet = new Set(resolutions.map(r => r.toLowerCase()));

  return (
    <div className="flex gap-1.5">
      {displayResolutions.map(r => {
        const isAvailable = availableSet.has(r.toLowerCase());
        const isActive = activeResolution?.toLowerCase() === r.toLowerCase();
        return (
          <button
            key={r}
            onClick={() => isAvailable && onSelect(r)}
            className={`flex-1 py-1.5 rounded-lg text-[11px] font-medium transition-all border ${
              isActive
                ? 'bg-white/15 text-white border-white/30'
                : isAvailable
                  ? 'text-white/50 border-white/10 hover:border-white/20 hover:bg-white/5'
                  : 'text-white/20 border-white/5 cursor-not-allowed'
            }`}
          >
            {r.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
};

interface CountSelectorProps {
  counts: number[];
  activeCount?: number;
  onSelect: (count: number) => void;
  label?: string;
}

export const CountSelector: React.FC<CountSelectorProps> = ({
  counts, activeCount, onSelect, label,
}) => (
  <div className="flex gap-1.5">
    {counts.map(c => (
      <button
        key={c}
        onClick={() => onSelect(c)}
        className={`flex-1 py-1.5 rounded-lg text-[11px] font-medium transition-all border ${activeCount === c ? 'bg-white/15 text-white border-white/30' : 'text-white/50 border-white/10 hover:border-white/20 hover:bg-white/5'}`}
      >
        {label ? `${c} ${label}` : c}
      </button>
    ))}
  </div>
);

interface StrengthSelectorProps {
  strengths: { value: number; label: string }[];
  activeStrength?: number;
  onSelect: (strength: number) => void;
}

export const StrengthSelector: React.FC<StrengthSelectorProps> = ({
  strengths, activeStrength, onSelect,
}) => (
  <div className="flex gap-1.5">
    {strengths.map(s => (
      <button
        key={s.value}
        onClick={() => onSelect(s.value)}
        className={`flex-1 py-1.5 rounded-lg text-[11px] font-medium transition-all border ${activeStrength === s.value ? 'bg-white/15 text-white border-white/30' : 'text-white/50 border-white/10 hover:border-white/20 hover:bg-white/5'}`}
      >
        {s.label}
      </button>
    ))}
  </div>
);

interface ParamSectionProps {
  label: string;
  children: React.ReactNode;
  className?: string;
}

export const ParamSection: React.FC<ParamSectionProps> = ({ label, children, className = '' }) => (
  <div className={`mb-3 ${className}`}>
    <div className="text-[10px] text-white/40 mb-2 tracking-wide">{label}</div>
    {children}
  </div>
);

export const IMAGE_ASPECT_RATIOS = ['1:1', '3:4', '4:3', '9:16', '16:9', '3:2', '2:3', '5:4', '4:5', '21:9'];
export const VIDEO_ASPECT_RATIOS = ['1:1', '3:4', '4:3', '9:16', '16:9'];
export const IMAGE_RESOLUTIONS = ['1k', '2k', '4k'];
export const UPSCALE_RESOLUTIONS = ['2k', '4k', '8k'];
export const VIDEO_RESOLUTIONS = ['480p', '720p', '1080p'];
export const IMAGE_COUNTS = [1, 2, 3, 4];
export const VIDEO_COUNTS = [1, 2, 3, 4];
export const UPSCALE_STRENGTHS = [
  { value: 0.3, label: '轻微' },
  { value: 0.5, label: '适中' },
  { value: 0.7, label: '较强' },
  { value: 1.0, label: '最强' },
];
